// NOTICE: KAPTION-INTEGRATION
// whatsapp-call-transcripts.WEB.T2 / WEB.T3 — playback state shared by the
// call player (KaptionCallPlayer) and the transcript reader
// (KaptionCallTranscript). Ported from the desktop reader (kaptionai
// desktop/shell/rail.js highlightTurn / highlightWord, ~1425-1483):
//  - the turn under the playhead (words first, then the shortest turn that
//    contains it, then the last one started) is "now" and scrolls into view
//    while playing — unless the user scrolled in the last 4 s;
//  - the word under the playhead is found by binary search and held 0.35 s
//    into the gap after it; an approximate word holds nothing.
// The playhead is read on `timeupdate` and, while playing, once per frame,
// so the word highlight keeps up with speech (timeupdate fires ~4×/s).
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
import {
  pairTurnWords,
  turnAt,
  wordAt,
} from 'dashboard/components-next/kaption/calls/kaptionCallTranscriptModel';

export const USER_SCROLL_PAUSE_MS = 4000;
// call-transcript-search.WEB.3 — a jump lands 1 s early so the sentence is heard whole.
export const JUMP_PREROLL_SECONDS = 1;

export const useKaptionTranscriptPlayback = (
  transcript,
  { now = () => Date.now() } = {}
) => {
  const audio = shallowRef(null);
  const currentTime = ref(0);
  const duration = ref(0);
  const playing = ref(false);
  const rate = ref(1);
  // Nothing is lit until the user plays or seeks (desktop: `active`).
  const active = ref(false);
  const turnEls = new Map();
  let lastUserScroll = -Infinity;
  let frame = 0;

  const rows = computed(() =>
    (transcript.value?.turns || []).map(turn => ({
      turn,
      ...pairTurnWords(turn),
    }))
  );

  const currentRow = computed(() =>
    active.value ? turnAt(rows.value, currentTime.value) : -1
  );
  const currentWord = computed(() => {
    const row = rows.value[currentRow.value];
    if (!row || !row.timed) return -1;
    return wordAt(row.tokens, currentTime.value);
  });

  const totalSeconds = computed(
    () => duration.value || transcript.value?.durationSeconds || 0
  );

  const update = seconds => {
    if (Number.isFinite(seconds)) currentTime.value = seconds;
  };

  const stopFrames = () => {
    if (frame && typeof cancelAnimationFrame === 'function')
      cancelAnimationFrame(frame);
    frame = 0;
  };
  const step = () => {
    if (!audio.value || !playing.value) return;
    update(audio.value.currentTime);
    frame = requestAnimationFrame(step);
  };
  const startFrames = () => {
    stopFrames();
    if (typeof requestAnimationFrame === 'function')
      frame = requestAnimationFrame(step);
  };

  const handlers = {
    timeupdate: () => update(audio.value?.currentTime),
    seeked: () => update(audio.value?.currentTime),
    play: () => {
      playing.value = true;
      active.value = true;
      startFrames();
    },
    pause: () => {
      playing.value = false;
      stopFrames();
      update(audio.value?.currentTime);
    },
    ended: () => {
      playing.value = false;
      stopFrames();
    },
    loadedmetadata: () => {
      const d = audio.value?.duration;
      if (Number.isFinite(d) && d > 0) duration.value = d;
    },
  };
  handlers.durationchange = handlers.loadedmetadata;

  const detach = el => {
    if (!el) return;
    Object.entries(handlers).forEach(([event, handler]) =>
      el.removeEventListener(event, handler)
    );
  };
  const attach = el => {
    if (audio.value === el) return;
    detach(audio.value);
    audio.value = el || null;
    if (!el) return;
    Object.entries(handlers).forEach(([event, handler]) =>
      el.addEventListener(event, handler)
    );
    el.playbackRate = rate.value;
    handlers.loadedmetadata();
    // A seek asked for before the audio existed (deep link `?t=`).
    if (currentTime.value > 0) {
      try {
        el.currentTime = currentTime.value;
      } catch (e) {
        // metadata not loaded yet; loadedmetadata keeps currentTime
      }
    }
  };

  const play = async () => {
    active.value = true;
    if (!audio.value) return;
    try {
      await audio.value.play();
    } catch (e) {
      // autoplay refused or no source; the controls stay usable
    }
  };
  const pause = () => audio.value?.pause();
  const toggle = () => (playing.value ? pause() : play());

  // whatsapp-call-transcripts.WEB.T2 — click a word/turn/lane: seek and play.
  // `{play: false}` only moves the playhead (deep link: loads paused).
  const seek = (seconds, { play: autoplay = true } = {}) => {
    const target = Math.max(0, Number(seconds) || 0);
    active.value = true;
    update(target);
    if (audio.value) {
      try {
        audio.value.currentTime = target;
      } catch (e) {
        // not seekable yet; kept in currentTime for attach()
      }
    }
    if (autoplay) play();
  };

  const setRate = value => {
    rate.value = value;
    if (audio.value) audio.value.playbackRate = value;
  };
  const cycleRate = () => {
    const rates = [1, 1.25, 1.5, 2];
    setRate(rates[(rates.indexOf(rate.value) + 1) % rates.length]);
  };

  // WEB.T3 — the reader reports user scrolls (wheel, touch, keys).
  const noteUserScroll = () => {
    lastUserScroll = now();
  };
  const registerTurn = (rowIndex, el) => {
    if (el) turnEls.set(rowIndex, el);
    else turnEls.delete(rowIndex);
  };
  const scrollToRow = (rowIndex, { force = false } = {}) => {
    if (!force && now() - lastUserScroll < USER_SCROLL_PAUSE_MS) return false;
    const el = turnEls.get(rowIndex);
    if (!el?.scrollIntoView) return false;
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    return true;
  };

  watch(currentRow, index => {
    if (index >= 0 && playing.value) scrollToRow(index);
  });

  onBeforeUnmount(() => {
    stopFrames();
    detach(audio.value);
  });

  return {
    audio,
    attach,
    rows,
    currentTime,
    duration: totalSeconds,
    playing,
    rate,
    active,
    currentRow,
    currentWord,
    play,
    pause,
    toggle,
    seek,
    setRate,
    cycleRate,
    update,
    noteUserScroll,
    registerTurn,
    scrollToRow,
  };
};
